import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faBullhorn, faComments, faVideo, faBook } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../utils/http';
import AnnouncementsPanel from '../components/course/AnnouncementsPanel';
import DiscussionsPanel from '../components/course/DiscussionsPanel';
import WebinarsPanel from '../components/course/WebinarsPanel';

interface CourseRef {
  learnershipId: string;
  title: string;
  saqaId: string;
  canManage: boolean;
}

type Tab = 'announcements' | 'discussions' | 'sessions';

/** Learner hub: announcements, discussion boards and live sessions for each enrolled course. */
export default function LearningHubPage() {
  const { accessToken } = useAuth();
  const [courses, setCourses] = useState<CourseRef[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [tab, setTab] = useState<Tab>('announcements');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      const res = await authFetch<CourseRef[]>(accessToken, '/api/community/my-courses');
      if (res.ok && res.data) {
        setCourses(res.data);
        if (res.data.length > 0) setSelected(res.data[0].learnershipId);
      } else {
        setError(res.message || 'Failed to load your courses.');
      }
      setIsLoading(false);
    })();
  }, [accessToken]);

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: 'announcements', label: 'Announcements', icon: faBullhorn },
    { id: 'discussions', label: 'Discussions', icon: faComments },
    { id: 'sessions', label: 'Live Sessions', icon: faVideo },
  ];

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Learning Hub</h1>
        <p className="text-sm text-brand-900/60 mt-1">Announcements, discussions and live sessions from your facilitators</p>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {!isLoading && !error && courses.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faBook} className="h-12 w-12 text-brand-300 mb-4" />
            <p className="text-brand-900/60">You are not enrolled in any learnerships yet.</p>
          </div>
        )}

        {!isLoading && courses.length > 0 && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-brand-950 mb-2">Learnership</label>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full sm:w-96 px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {courses.map((c) => (
                  <option key={c.learnershipId} value={c.learnershipId}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-1 border-b border-canvas-300 bg-white rounded-t-2xl mb-6 overflow-x-auto">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
                    tab === t.id ? 'border-brand-600 text-brand-700' : 'border-transparent text-brand-900/60 hover:text-brand-900'
                  }`}
                >
                  <FontAwesomeIcon icon={t.icon} className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>

            {selected && tab === 'announcements' && <AnnouncementsPanel learnershipId={selected} canManage={false} />}
            {selected && tab === 'discussions' && <DiscussionsPanel learnershipId={selected} />}
            {selected && tab === 'sessions' && <WebinarsPanel learnershipId={selected} canManage={false} />}
          </>
        )}
      </div>
    </>
  );
}
